import React, { useMemo } from 'react';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import { Link } from 'react-router-dom';
import Badge from '../../components/base/Badge';
import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';

function sliceId(id, n = 8) {
  if (!id) return '-';
  return String(id).slice(0, 8);
}

export const machineTableColumns = [
  {
    accessorKey: 'name',
    header: 'Machine',
    cell: ({ row: { original } }) => {
      const { name } = original;
      return (
        <Link
          to={`/apps/machine-management/machine-queue/${name}`}
          className="text-decoration-none fw-bold fs-8"
        >
          {name}
        </Link>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '10%' } }
    }
  },

  {
    id: 'status',
    header: 'Status',
    accessorFn: row => row.status?.label ?? row.status ?? 'UNKNOWN',
    cell: ({ row: { original } }) => {
      // const badge = mapStatusToBadge(original.status);
      return (
        <Badge variant="phoenix" bg={original.status.type}>
          {original.status.label}
        </Badge>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '10%' } }
    }
  },

  {
    header: 'Start date',
    accessorKey: 'start_machine_date',
    cell: ({ row: { original } }) => original.start_machine_date ?? '-',
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '14%' } }
    }
  },

  {
    header: 'Stopped date',
    accessorKey: 'stopped_machine_date',
    cell: ({ row: { original } }) => original.stopped_machine_date ?? '-',
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '14%' } }
    }
  },

  {
    header: 'Prod ID',
    accessorKey: 'jobId',
    cell: ({ row: { original } }) => {
      const shortId = sliceId(original.jobId, 8);

      return (
        <Link
          to={`/apps/project-management/project-details/${original.jobId}`}
          className="text-decoration-none fw-bold fs-8"
        >
          {shortId}
        </Link>
      );
    },
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '8%' } }
    }
  },

  {
    header: 'Product',
    accessorKey: 'product',
    cell: ({ row: { original } }) => original.product ?? '-',
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '16%' } }
    }
  },

  {
    header: 'Phase',
    accessorKey: 'phase',
    cell: ({ row: { original } }) => original.phase ?? '-',
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '12%' } }
    }
  },

  {
    header: 'Planned (m)',
    accessorKey: 'plannedLength',
    cell: ({ row: { original } }) =>
      typeof original.plannedLength === 'number' ? original.plannedLength : '-',
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '10%' } }
    }
  },

  {
    header: 'Operator',
    accessorKey: 'operator',
    cell: ({ row: { original } }) => original.operator ?? '-',
    meta: {
      cellProps: { className: 'white-space-nowrap py-3' },
      headerProps: { style: { width: '10%' } }
    }
  }
];

const MachinesMonitorTable = ({ queue = [] }) => {
  // transformăm structura venită din API:
  // item = { name, status, start_machine_date, stopped_machine_date, operator, queue: { jobId, product, task, ... } }
  const rows = useMemo(() => {
    return (queue || []).map(m => {
      const head = m.queue || null; // tu ai pus head în `queue`
      return {
        name: m.name,
        status: m.status,
        start_machine_date: m.start_machine_date,
        stopped_machine_date: m.stopped_machine_date,
        operator: m.operator,

        // din head:
        jobId: head?.jobId ?? null,
        product: head?.product ?? null,
        phase: head?.task?.phase ?? null,
        plannedLength: head?.task?.plannedLength ?? null
      };
    });
  }, [queue]);

  const table = useAdvanceTable({
    data: rows,
    columns: machineTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  return (
    <AdvanceTableProvider {...table}>
      <AdvanceTable
        tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }}
      />
      <AdvanceTableFooter pagination />
    </AdvanceTableProvider>
  );
};

export default MachinesMonitorTable;
